import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  bookingsAPI,
  adminAPI,
  servicesAPI
} from '../utils/api';
import { toast } from 'react-toastify';
import Calendar from 'react-calendar';
import RoomIcon, { inferRoomIcon, ROOM_ICON_OPTIONS } from '../components/RoomIcon';
import 'react-calendar/dist/Calendar.css';
import '../styles/Admin.css';

const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const formatDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

const halfHourTimes = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2).toString().padStart(2, '0');
  const minute = index % 2 === 0 ? '00' : '30';
  return `${hour}:${minute}`;
});

const emptyServiceForm = {
  id: '',
  nameEn: '',
  nameFr: '',
  descriptionEn: '',
  descriptionFr: '',
  featuresEn: '',
  featuresFr: '',
  basePrice: '',
  duration: '',
  maxBookingsPerSlot: '1',
  affectsOtherServices: false,
  configuratorEnabled: false,
  configurationItems: []
};

const Admin = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [bookingStartTimes, setBookingStartTimes] = useState({});
  const [bookingCollaborators, setBookingCollaborators] = useState({});
  const [availableCollaborators, setAvailableCollaborators] = useState({});
  const [viewedAvailability, setViewedAvailability] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [settings, setSettings] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [serviceForm, setServiceForm] = useState(emptyServiceForm);
  const filteredBookings = statusFilter === 'all'
    ? bookings
    : bookings.filter((booking) => booking.status === statusFilter);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.getAll();
      setBookings(response.data);
      setBookingStartTimes(Object.fromEntries(response.data.map((booking) => {
        if (booking.status !== 'confirmed') return [booking._id, ''];
        const date = new Date(booking.dateTime);
        return [booking._id, `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`];
      })));
    } catch (err) {
      toast.error(t('admin.loadBookingsError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSettings();
      setSettings(response.data);
    } catch (err) {
      toast.error(t('admin.loadSettingsError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (err) {
      toast.error(t('services.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      toast.error(t('admin.loadUsersError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/');
      return;
    }

    if (activeTab === 'bookings') {
      fetchBookings();
    } else if (activeTab === 'services') {
      fetchServices();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'settings') {
      fetchSettings();
    }
  }, [activeTab, fetchBookings, fetchServices, fetchSettings, fetchUsers, isAuthenticated, navigate, user]);

  const handleBookingStatusUpdate = async (bookingId, status) => {
    const startTime = bookingStartTimes[bookingId] || '';
    if (status === 'confirmed' && !startTime) {
      toast.error(t('admin.startTimeRequired'));
      return;
    }
    const collaboratorId = bookingCollaborators[bookingId] || '';
    if (status === 'confirmed' && !collaboratorId) {
      toast.error(t('admin.collaboratorRequired'));
      return;
    }
    try {
      await bookingsAPI.updateStatus(bookingId, status, '', startTime, collaboratorId);
      toast.success(t('admin.bookingUpdated'));
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.message || t('admin.bookingUpdateError'));
    }
  };

  const loadAvailableCollaborators = async (bookingId, startTime) => {
    setBookingStartTimes((current) => ({ ...current, [bookingId]: startTime }));
    setBookingCollaborators((current) => ({ ...current, [bookingId]: '' }));
    if (!startTime) return setAvailableCollaborators((current) => ({ ...current, [bookingId]: [] }));
    try {
      const { data } = await adminAPI.getAvailableCollaborators(bookingId, startTime);
      setAvailableCollaborators((current) => ({ ...current, [bookingId]: data }));
    } catch (error) {
      setAvailableCollaborators((current) => ({ ...current, [bookingId]: [] }));
    }
  };

  const handleUserRoleChange = async (userId, role) => {
    try { await adminAPI.updateUserRole(userId, role); toast.success(t('admin.roleUpdated')); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.message || t('admin.roleUpdateError')); }
  };

  const viewCollaboratorAvailability = async (registeredUser) => {
    try {
      const { data } = await adminAPI.getUserAvailability(registeredUser._id);
      setViewedAvailability({ user: registeredUser, slots: data.availableSlots || [] });
    } catch { toast.error(t('collaborator.loadError')); }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm(t('admin.deleteBookingConfirm'))) return;
    try {
      await bookingsAPI.removeAdmin(bookingId);
      toast.success(t('admin.bookingDeleted'));
      fetchBookings();
    } catch (err) {
      toast.error(t('admin.bookingDeleteError'));
    }
  };

  const handleAddAlertEmail = async (e) => {
    e.preventDefault();

    if (!newEmail) {
      toast.error(t('admin.enterEmailError'));
      return;
    }

    try {
      await adminAPI.addAlertEmail(newEmail);
      toast.success(t('admin.emailAdded'));
      setNewEmail('');
      fetchSettings();
    } catch (err) {
      toast.error(t('admin.emailAddError'));
    }
  };

  const handleRemoveAlertEmail = async (emailId) => {
    try {
      await adminAPI.removeAlertEmail(emailId);
      toast.success(t('admin.emailRemoved'));
      fetchSettings();
    } catch (err) {
      toast.error(t('admin.emailRemoveError'));
    }
  };

  const handleWorkingHourChange = (day, field, value) => {
    setSettings((current) => ({
      ...current,
      workingHours: {
        ...current.workingHours,
        [day]: {
          ...current.workingHours[day],
          [field]: value
        }
      }
    }));
  };

  const handleSaveWorkingHours = async () => {
    try {
      const response = await adminAPI.updateWorkingHours(settings.workingHours);
      setSettings(response.data.settings);
      toast.success(t('admin.workingHoursUpdated'));
    } catch (err) {
      toast.error(t('admin.workingHoursUpdateError'));
    }
  };

  const getDateOverride = (date) => settings?.availableSlots?.find(
    (slot) => formatDateKey(new Date(slot.date)) === formatDateKey(date)
  );

  const getAdminDateCapacity = (date) => {
    const override = getDateOverride(date);
    if (override) return Number(override.capacity ?? (override.isAvailable ? 2 : 0));
    const hours = settings?.workingHours?.[dayNames[date.getDay()]];
    return hours?.start && hours?.end ? 2 : 0;
  };

  const handleToggleDate = (date) => {
    const dateKey = formatDateKey(date);
    const hours = settings?.workingHours?.[dayNames[date.getDay()]];
    const currentCapacity = getAdminDateCapacity(date);
    const nextCapacity = currentCapacity === 2 ? 1 : currentCapacity === 1 ? 0 : 2;
    if (nextCapacity > 0 && (!hours?.start || !hours?.end)) {
      toast.error(t('admin.configureDayHours'));
      return;
    }

    setSettings((current) => ({
      ...current,
      availableSlots: [
        ...(current.availableSlots || []).filter(
          (slot) => formatDateKey(new Date(slot.date)) !== dateKey
        ),
        {
          date: `${dateKey}T12:00:00`,
          startTime: hours?.start || '',
          endTime: hours?.end || '',
          isAvailable: nextCapacity > 0,
          capacity: nextCapacity
        }
      ]
    }));
  };

  const handleSaveAvailability = async () => {
    try {
      const response = await adminAPI.updateAvailability(settings.availableSlots || []);
      setSettings(response.data.settings);
      toast.success(t('admin.availabilityUpdated'));
    } catch (err) {
      toast.error(t('admin.availabilityUpdateError'));
    }
  };

  const handleServiceInput = (event) => {
    const { name, type, value, checked } = event.target;
    setServiceForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditService = (service) => {
    setServiceForm({
      id: service._id,
      nameEn: service.name?.en || '',
      nameFr: service.name?.fr || '',
      descriptionEn: service.description?.en || '',
      descriptionFr: service.description?.fr || '',
      featuresEn: service.features?.en?.join('\n') || '',
      featuresFr: service.features?.fr?.join('\n') || '',
      basePrice: service.basePrice,
      duration: service.duration,
      maxBookingsPerSlot: service.maxBookingsPerSlot || 1,
      affectsOtherServices: Boolean(service.affectsOtherServices),
      configuratorEnabled: Boolean(service.configuratorEnabled),
      configurationItems: (service.configurationItems || []).map((item) => ({
        id: item._id,
        icon: item.icon || inferRoomIcon(item),
        nameEn: item.name?.en || '',
        nameFr: item.name?.fr || '',
        defaultQuantity: item.defaultQuantity || 0,
        pricePerAdditional: item.pricePerAdditional || 0,
        minutesPerAdditional: item.minutesPerAdditional || 15
      }))
    });
  };

  useEffect(() => {
    if (activeTab === 'services' && services[0] && serviceForm.id !== services[0]._id) {
      handleEditService(services[0]);
    }
  }, [activeTab, services, serviceForm.id]);

  const handleSaveService = async (event) => {
    event.preventDefault();
    const payload = {
      name: { en: serviceForm.nameEn.trim(), fr: serviceForm.nameFr.trim() },
      description: {
        en: serviceForm.descriptionEn.trim(),
        fr: serviceForm.descriptionFr.trim()
      },
      features: {
        en: serviceForm.featuresEn.split('\n').map(item => item.trim()).filter(Boolean),
        fr: serviceForm.featuresFr.split('\n').map(item => item.trim()).filter(Boolean)
      },
      basePrice: Number(serviceForm.basePrice),
      duration: Number(serviceForm.duration),
      maxBookingsPerSlot: Number(serviceForm.maxBookingsPerSlot),
      affectsOtherServices: serviceForm.affectsOtherServices,
      configuratorEnabled: serviceForm.configuratorEnabled,
      configurationItems: serviceForm.configurationItems.map((item) => ({
        ...(item.id ? { _id: item.id } : {}),
        name: { en: item.nameEn.trim(), fr: item.nameFr.trim() },
        icon: item.icon || 'sparkles',
        defaultQuantity: Number(item.defaultQuantity),
        pricePerAdditional: Number(item.pricePerAdditional),
        minutesPerAdditional: Number(item.minutesPerAdditional)
      })),
      isActive: true
    };

    try {
      if (serviceForm.id) {
        await servicesAPI.update(serviceForm.id, payload);
      } else {
        await servicesAPI.create(payload);
      }
      toast.success(t('admin.serviceSaved'));
      setServiceForm(emptyServiceForm);
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.message || t('admin.serviceSaveError'));
    }
  };

  const handleConfigurationItemChange = (index, field, value) => {
    setServiceForm((current) => ({
      ...current,
      configurationItems: current.configurationItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleAddConfigurationItem = () => {
    setServiceForm((current) => ({
      ...current,
      configurationItems: [...current.configurationItems, {
        nameEn: '', nameFr: '', icon: 'sparkles', defaultQuantity: 0,
        pricePerAdditional: 0, minutesPerAdditional: 15
      }]
    }));
  };

  const handleRemoveConfigurationItem = (index) => {
    setServiceForm((current) => ({
      ...current,
      configurationItems: current.configurationItems.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const handleMoveConfigurationItem = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= serviceForm.configurationItems.length) return;
    setServiceForm((current) => {
      const items = [...current.configurationItems];
      [items[index], items[target]] = [items[target], items[index]];
      return { ...current, configurationItems: items };
    });
  };

  return (
    <div className="admin-page">
      <h1>{t('admin.dashboard')}</h1>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookings')}
        >
          {t('admin.bookingRequests')}
        </button>
        <button
          className={`tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          {t('admin.services')}
        </button>
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          {t('admin.users')}
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          {t('admin.settings')}
        </button>
      </div>

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="admin-content">
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : (
            <>
              <div className="booking-filters">
                <label htmlFor="status-filter">{t('admin.filterByStatus')}</label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">{t('admin.allStatuses')}</option>
                  <option value="pending">{t('admin.pending')}</option>
                  <option value="confirmed">{t('admin.confirmed')}</option>
                  <option value="completed">{t('admin.completed')}</option>
                  <option value="cancelled">{t('admin.cancelled')}</option>
                </select>
                <span>{t('admin.bookingCount', { count: filteredBookings.length })}</span>
              </div>
              <div className="bookings-table">
                <table>
                <thead>
                  <tr>
                    <th>{t('admin.date')}</th>
                    <th>{t('admin.service')}</th>
                    <th>{t('admin.customer')}</th>
                    <th>{t('profile.phone')}</th>
                    <th>{t('admin.address')}</th>
                    <th>{t('booking.notes')}</th>
                    <th>{t('admin.status')}</th>
                    <th>{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr key={booking._id}>
                      <td data-label={t('admin.date')}>
                        {booking.status === 'confirmed'
                          ? new Date(booking.dateTime).toLocaleString(i18n.resolvedLanguage)
                          : new Date(booking.dateTime).toLocaleDateString(i18n.resolvedLanguage)}
                      </td>
                      <td data-label={t('admin.service')}>
                        {i18n.resolvedLanguage?.startsWith('fr')
                          ? booking.serviceId?.name?.fr
                          : booking.serviceId?.name?.en}
                        {booking.serviceConfiguration?.length > 0 && (
                          <div className="booking-configuration-summary">
                            {booking.serviceConfiguration.filter((item) => Number(item.quantity) > 0).map((item) => (
                              <small key={item.optionId}>
                                {(i18n.resolvedLanguage?.startsWith('fr') ? item.name?.fr : item.name?.en) || item.name?.en}: {item.quantity}
                              </small>
                            ))}
                            <small><strong>{t('booking.estimatedDuration')}:</strong> {Math.floor(booking.duration)}h {Math.round((booking.duration % 1) * 60)}min</small>
                          </div>
                        )}
                      </td>
                      <td data-label={t('admin.customer')}>{booking.userId?.name}</td>
                      <td data-label={t('profile.phone')}>{booking.contactPhone || booking.userId?.phone || '—'}</td>
                      <td data-label={t('admin.address')}>
                        {booking.cleaningAddress
                          ? [
                              booking.cleaningAddress.street,
                              booking.cleaningAddress.unit,
                              booking.cleaningAddress.city,
                              booking.cleaningAddress.province,
                              booking.cleaningAddress.postalCode
                            ].filter(Boolean).join(', ')
                          : '—'}
                      </td>
                      <td data-label={t('booking.notes')} className="customer-notes-cell">{booking.clientNotes || '—'}</td>
                      <td data-label={t('admin.status')} className={`status ${booking.status}`}>
                        {t(`admin.${booking.status}`)}
                      </td>
                      <td data-label={t('admin.actions')} className="booking-actions-cell">
                        <div className="booking-admin-actions">
                          {(booking.status === 'pending' || booking.status === 'confirmed') && (
                            <label className="admin-start-time">
                              <span>{t('admin.approximateStartTime')}</span>
                              <input
                                type="time"
                                value={bookingStartTimes[booking._id] || ''}
                                onChange={(e) => loadAvailableCollaborators(booking._id, e.target.value)}
                              />
                            </label>
                          )}
                          {(booking.status === 'pending' || booking.status === 'confirmed') && (
                            <label className="admin-collaborator-select">
                              <span>{t('admin.assignedCollaborator')}</span>
                              <select value={bookingCollaborators[booking._id] || booking.assignedCollaborator?._id || ''} onChange={(e) => setBookingCollaborators((current) => ({ ...current, [booking._id]: e.target.value }))}>
                                <option value="">{t('admin.selectCollaborator')}</option>
                                {(availableCollaborators[booking._id] || (booking.assignedCollaborator ? [booking.assignedCollaborator] : [])).map((collaborator) => <option key={collaborator._id} value={collaborator._id}>{collaborator.name}</option>)}
                              </select>
                            </label>
                          )}
                          <select
                            value={booking.status}
                            onChange={(e) =>
                              handleBookingStatusUpdate(booking._id, e.target.value)
                            }
                          >
                            <option value="pending">{t('admin.pending')}</option>
                            <option value="confirmed">{t('admin.confirmed')}</option>
                            <option value="completed">{t('admin.completed')}</option>
                            <option value="cancelled">{t('admin.cancelled')}</option>
                          </select>
                          <button type="button" className="delete-booking-btn" onClick={() => handleDeleteBooking(booking._id)}>
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
              {filteredBookings.length === 0 && (
                <p className="empty-filter-result">{t('admin.noBookingsForStatus')}</p>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'services' && (
        <div className="admin-content services-admin">
          {loading ? <p>{t('common.loading')}</p> : <form className="service-editor" onSubmit={handleSaveService}>
            <h2>
              {t('admin.editRates')}
            </h2>
            <div className="service-form-grid">
              <label>
                {t('admin.titleEn')}
                <input name="nameEn" value={serviceForm.nameEn} onChange={handleServiceInput} required />
              </label>
              <label>
                {t('admin.titleFr')}
                <input name="nameFr" value={serviceForm.nameFr} onChange={handleServiceInput} required />
              </label>
              <label>
                {t('admin.descriptionEn')}
                <textarea name="descriptionEn" value={serviceForm.descriptionEn} onChange={handleServiceInput} rows="3" />
              </label>
              <label>
                {t('admin.descriptionFr')}
                <textarea name="descriptionFr" value={serviceForm.descriptionFr} onChange={handleServiceInput} rows="3" />
              </label>
              <label>
                {t('admin.detailsEn')}
                <textarea name="featuresEn" value={serviceForm.featuresEn} onChange={handleServiceInput} rows="5" placeholder={t('admin.onePerLine')} />
              </label>
              <label>
                {t('admin.detailsFr')}
                <textarea name="featuresFr" value={serviceForm.featuresFr} onChange={handleServiceInput} rows="5" placeholder={t('admin.onePerLine')} />
              </label>
              <label>
                {t('admin.price')}
                <input name="basePrice" type="number" min="0.01" step="0.01" value={serviceForm.basePrice} onChange={handleServiceInput} required />
              </label>
              <label>
                {t('admin.durationHours')}
                <input name="duration" type="number" min="0.5" step="0.5" value={serviceForm.duration} onChange={handleServiceInput} required />
              </label>
              <label>
                {t('admin.capacityPerSlot')}
                <input name="maxBookingsPerSlot" type="number" min="1" step="1" value={serviceForm.maxBookingsPerSlot} onChange={handleServiceInput} required />
              </label>
              <label className="shared-schedule-option">
                <input
                  name="affectsOtherServices"
                  type="checkbox"
                  checked={serviceForm.affectsOtherServices}
                  onChange={handleServiceInput}
                />
                <span>
                  <strong>{t('admin.affectsOtherServices')}</strong>
                  <small>{t('admin.affectsOtherServicesHelp')}</small>
                </span>
              </label>
              <div className="configuration-editor">
                    <div className="configuration-editor-heading">
                      <h3>{t('admin.configurationItems')}</h3>
                      <div>
                        <button type="button" onClick={handleAddConfigurationItem}>{t('admin.addConfigurationItem')}</button>
                      </div>
                    </div>
                    <div className="configuration-items-list">
                      {serviceForm.configurationItems.map((item, index) => (
                        <div className="configuration-item-row" key={item.id || index}>
                          <label className="configuration-icon-field"><RoomIcon item={item} /><span>{t('admin.itemIcon')}</span>
                            <select value={item.icon || 'sparkles'} onChange={(e) => handleConfigurationItemChange(index, 'icon', e.target.value)}>
                              {ROOM_ICON_OPTIONS.map((icon) => <option key={icon} value={icon}>{t(`icons.${icon}`)}</option>)}
                            </select>
                          </label>
                          <label>{t('admin.itemNameEn')}<input value={item.nameEn} onChange={(e) => handleConfigurationItemChange(index, 'nameEn', e.target.value)} required /></label>
                          <label>{t('admin.itemNameFr')}<input value={item.nameFr} onChange={(e) => handleConfigurationItemChange(index, 'nameFr', e.target.value)} required /></label>
                          <label>{t('admin.defaultQuantity')}<input type="number" min="0" max="30" value={item.defaultQuantity} onChange={(e) => handleConfigurationItemChange(index, 'defaultQuantity', e.target.value)} required /></label>
                          <label>{t('admin.additionalPrice')}<input type="number" min="0" step="0.01" value={item.pricePerAdditional} onChange={(e) => handleConfigurationItemChange(index, 'pricePerAdditional', e.target.value)} required /></label>
                          <label>{t('admin.additionalMinutes')}<input type="number" min="0" step="5" value={item.minutesPerAdditional} onChange={(e) => handleConfigurationItemChange(index, 'minutesPerAdditional', e.target.value)} required /></label>
                          <div className="configuration-item-actions">
                            <button type="button" onClick={() => handleMoveConfigurationItem(index, -1)} disabled={index === 0} title={t('admin.moveUp')}>↑</button>
                            <button type="button" onClick={() => handleMoveConfigurationItem(index, 1)} disabled={index === serviceForm.configurationItems.length - 1} title={t('admin.moveDown')}>↓</button>
                            <button type="button" className="danger-btn" onClick={() => handleRemoveConfigurationItem(index)}>{t('common.delete')}</button>
                          </div>
                        </div>
                      ))}
                    </div>
              </div>
            </div>
            <div className="service-editor-actions">
              <button type="submit">{t('common.save')}</button>
            </div>
          </form>}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="admin-content">
          <div className="section-heading users-heading">
            <h2>{t('admin.registeredUsers')}</h2>
            <span>{t('admin.userCount', { count: users.length })}</span>
          </div>
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : users.length === 0 ? (
            <p className="empty-filter-result">{t('admin.noUsers')}</p>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('auth.name')}</th>
                    <th>{t('auth.email')}</th>
                    <th>{t('profile.phone')}</th>
                    <th>{t('admin.role')}</th>
                    <th>{t('admin.accountStatus')}</th>
                    <th>{t('admin.registeredAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((registeredUser) => (
                    <tr key={registeredUser._id}>
                      <td data-label={t('auth.name')}>{registeredUser.name || '—'}</td>
                      <td data-label={t('auth.email')}><a href={`mailto:${registeredUser.email}`}>{registeredUser.email}</a></td>
                      <td data-label={t('profile.phone')}>{registeredUser.phone || '—'}</td>
                      <td data-label={t('admin.role')}>
                        <div className="user-role-controls">
                          <select value={registeredUser.role} onChange={(e) => handleUserRoleChange(registeredUser._id, e.target.value)}>
                            <option value="customer">{t('admin.roles.customer')}</option>
                            <option value="collaborator">{t('admin.roles.collaborator')}</option>
                            <option value="admin">{t('admin.roles.admin')}</option>
                          </select>
                          {registeredUser.role === 'collaborator' && <button type="button" className="view-availability-btn" onClick={() => viewCollaboratorAvailability(registeredUser)}>{t('admin.viewAvailability')}</button>}
                        </div>
                      </td>
                      <td data-label={t('admin.accountStatus')}>
                        <span className={`user-status ${registeredUser.isActive ? 'active' : 'inactive'}`}>
                          {registeredUser.isActive ? t('admin.active') : t('admin.inactive')}
                        </span>
                      </td>
                      <td data-label={t('admin.registeredAt')}>{new Date(registeredUser.createdAt).toLocaleDateString(i18n.resolvedLanguage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {viewedAvailability && <section className="admin-collaborator-agenda">
            <div><h3>{t('admin.availabilityOf', { name: viewedAvailability.user.name })}</h3><button type="button" onClick={() => setViewedAvailability(null)}>×</button></div>
            {viewedAvailability.slots.length === 0 ? <p>{t('admin.noAvailability')}</p> : <ul>{viewedAvailability.slots.filter((slot) => slot.isAvailable).sort((a,b) => new Date(a.date)-new Date(b.date)).map((slot) => <li key={slot._id || slot.date}>{new Date(slot.date).toLocaleDateString(i18n.resolvedLanguage)} — {slot.startTime}–{slot.endTime}</li>)}</ul>}
          </section>}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="admin-content">
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : (
            <div className="settings-section">
              <h2>{t('admin.alertEmails')}</h2>

              <form onSubmit={handleAddAlertEmail} className="add-email-form">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={t('admin.enterEmail')}
                  required
                />
                <button type="submit">{t('admin.addEmail')}</button>
              </form>

              <div className="email-list">
                {settings?.alertEmails?.map((email) => (
                  <div key={email._id} className="email-item">
                    <span>{email.email}</span>
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveAlertEmail(email._id)}
                    >
                      {t('admin.remove')}
                    </button>
                  </div>
                ))}
              </div>

              <h2 style={{ marginTop: '2rem' }}>{t('admin.workingHours')}</h2>
              <p className="info-text">
                {t('admin.workingHoursInfo')}
              </p>

              <div className="working-hours-info">
                {settings?.workingHours && (
                  Object.entries(settings.workingHours).map(([day, hours]) => (
                    <div key={day} className="hour-item">
                      <span className="day">{t(`admin.days.${day}`)}:</span>
                      <div className="hour-controls">
                        <label className="time-field">
                          <span>{t('admin.start')}</span>
                          <select
                            value={hours.start || ''}
                            onChange={(e) =>
                              handleWorkingHourChange(day, 'start', e.target.value)
                            }
                          >
                            <option value="">{t('admin.closed')}</option>
                            {halfHourTimes.map((time) => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                        </label>
                        <span className="time-separator">–</span>
                        <label className="time-field">
                          <span>{t('admin.end')}</span>
                          <select
                            value={hours.end || ''}
                            onChange={(e) =>
                              handleWorkingHourChange(day, 'end', e.target.value)
                            }
                          >
                            <option value="">{t('admin.closed')}</option>
                            {halfHourTimes.map((time) => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <button
                type="button"
                className="save-hours-btn"
                onClick={handleSaveWorkingHours}
                disabled={!settings?.workingHours}
              >
                {t('admin.saveWorkingHours')}
              </button>

              <div className="availability-calendar-section">
                <h2>{t('admin.availableDates')}</h2>
                <p className="info-text">{t('admin.availableDatesInfo')}</p>
                <div className="availability-calendar-legend">
                  <span><i className="admin-date-capacity-two" />{t('admin.twoPeople')}</span>
                  <span><i className="admin-date-capacity-one" />{t('admin.onePerson')}</span>
                  <span><i className="admin-date-capacity-zero" />{t('admin.noPeople')}</span>
                </div>
                <div className="admin-calendar-grid">
                  {[0, 1, 2].map((monthOffset) => {
                    const month = new Date();
                    month.setDate(1);
                    month.setMonth(month.getMonth() + monthOffset);
                    return (
                      <div className="admin-calendar-month" key={formatDateKey(month)}>
                        <h3>{month.toLocaleDateString(i18n.resolvedLanguage?.startsWith('fr') ? 'fr-CA' : 'en-CA', { month: 'long', year: 'numeric' })}</h3>
                        <Calendar
                          activeStartDate={month}
                          showNavigation={false}
                          showNeighboringMonth={false}
                          locale={i18n.resolvedLanguage?.startsWith('fr') ? 'fr-CA' : 'en-CA'}
                          minDetail="month"
                          tileDisabled={({ date, view }) =>
                            view === 'month' && date < new Date(new Date().setHours(0, 0, 0, 0))
                          }
                          tileClassName={({ date, view }) =>
                            view === 'month'
                              ? `admin-calendar-capacity-${getAdminDateCapacity(date)}`
                              : null
                          }
                          onClickDay={handleToggleDate}
                        />
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="save-hours-btn" onClick={handleSaveAvailability}>
                  {t('admin.saveAvailableDates')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Admin;
